import React from 'react';

/**
 * EGGS Text Component
 * Based on TWK Burns typography specifications
 * 
 * @param {string} type - Heading | Body | Caption
 * @param {string} variant - h1|h2|h3|h4|h5|h6 (Heading) | lg|Base|sm|xs (Body/Caption)
 * @param {string} weight - Default | Strong (Body only)
 * @param {string} as - HTML element to render (h1, h2, p, span, etc.)
 * @param {React.ReactNode} children - Text content
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional element props
 */
export default function Text({
  type = 'Body',
  variant = 'Base',
  weight = 'Default',
  as,
  children,
  className = '',
  ...props
}) {
  // Determine the HTML element to render
  const getElement = () => {
    if (as) return as;
    if (type === 'Heading') return variant.toLowerCase();
    return 'p';
  };

  const Element = getElement();

  // Base classes for all text
  const baseClasses = [
    'font-burns',
    'text-eggs-dark', // EGGS Dark
    'm-0' // Reset default margins
  ].join(' ');

  // Get classes based on type and variant
  const getTypeClasses = () => {
    if (type === 'Heading') {
      const headingClasses = {
        h1: [
          'text-[64px]',
          'md:text-[48px]', // Mobile responsive
          'font-medium',
          'leading-[1.1]',
          'tracking-[-1.5px]'
        ].join(' '),
        
        h2: [
          'text-[32px]',
          'md:text-[28px]', // Mobile responsive
          'font-medium',
          'leading-[1.2]',
          'tracking-[-0.5px]'
        ].join(' '),
        
        h3: [
          'text-[24px]',
          'md:text-[22px]', // Mobile responsive
          'font-medium',
          'leading-[1.2]',
          'tracking-[0px]'
        ].join(' '),
        
        h4: [
          'text-[20px]',
          'font-medium',
          'leading-[1.2]',
          'tracking-[0.25px]'
        ].join(' '),
        
        h5: [
          'text-[18px]',
          'font-medium',
          'leading-[1.3]',
          'tracking-[0px]'
        ].join(' '),
        
        h6: [
          'text-[14px]',
          'font-medium',
          'leading-[1.3]',
          'tracking-[0.15px]'
        ].join(' ')
      };
      
      return headingClasses[variant] || headingClasses.h1;
    }

    if (type === 'Caption') {
      const captionClasses = {
        lg: [
          'text-[20px]',
          'font-extralight',
          'leading-[1.66]',
          'tracking-[0.4px]',
          'uppercase'
        ].join(' '),
        
        Base: [
          'text-[18px]',
          'md:text-[16px]', // Mobile responsive
          'font-extralight',
          'leading-[1.66]',
          'tracking-[0.4px]',
          'uppercase'
        ].join(' '),
        
        sm: [
          'text-[14px]',
          'font-extralight',
          'leading-[1.66]',
          'tracking-[0.4px]',
          'uppercase'
        ].join(' '),
        
        xs: [
          'text-[12px]',
          'font-extralight',
          'leading-[1.66]',
          'tracking-[0.4px]',
          'uppercase'
        ].join(' ')
      };
      
      return captionClasses[variant] || captionClasses.Base;
    }

    // Body text (default)
    const bodyClasses = {
      lg: [
        'text-[20px]',
        'leading-[1.4]',
        'tracking-[0.15px]',
        weight === 'Strong' ? 'font-normal' : 'font-extralight'
      ].join(' '),
      
      Base: [
        'text-[18px]',
        'md:text-[16px]', // Mobile responsive
        'leading-[1.4]',
        'tracking-[0.15px]',
        weight === 'Strong' ? 'font-normal' : 'font-extralight'
      ].join(' '),
      
      sm: [
        'text-[14px]',
        'leading-[1.4]',
        'tracking-[0.17px]',
        weight === 'Strong' ? 'font-normal' : 'font-extralight'
      ].join(' '),
      
      xs: [
        'text-[12px]',
        'leading-[1.4]',
        'tracking-[0.17px]',
        weight === 'Strong' ? 'font-normal' : 'font-extralight'
      ].join(' ')
    };
    
    return bodyClasses[variant] || bodyClasses.Base;
  };

  const typeClasses = getTypeClasses();

  return (
    <Element
      className={`${baseClasses} ${typeClasses} ${className}`}
      {...props}
    >
      {children}
    </Element>
  );
}

// Export convenience components for common use cases
export const Heading = ({ variant = 'h1', ...props }) => (
  <Text type="Heading" variant={variant} {...props} />
);

export const Body = ({ variant = 'Base', weight = 'Default', ...props }) => (
  <Text type="Body" variant={variant} weight={weight} {...props} />
);

export const Caption = ({ variant = 'Base', ...props }) => (
  <Text type="Caption" variant={variant} {...props} />
);

// Specific heading components
export const H1 = (props) => <Heading variant="h1" {...props} />;
export const H2 = (props) => <Heading variant="h2" {...props} />;
export const H3 = (props) => <Heading variant="h3" {...props} />;
export const H4 = (props) => <Heading variant="h4" {...props} />;
export const H5 = (props) => <Heading variant="h5" {...props} />;
export const H6 = (props) => <Heading variant="h6" {...props} />;